import { computed } from 'vue'
import { useAuthStore } from '~/store/auth'
import { usePackages } from '~/api/queries/packages'
import { TIER_FEATURES, type FeatureLimits, type Tier } from '~/constants/tier-features'
import type { Package } from '~/api/queries/packages'

export type { FeatureLimits, Tier }

export const usePackageFeatures = () => {
  const authStore = useAuthStore()
  const { data: packagesData } = usePackages()

  const userPackage = computed((): Package | null => {
    const user = authStore.getUser
    if (!user?.packageId || !packagesData.value?.data) {
      return null
    }
    return packagesData.value.data.find((pkg: Package) => pkg.id === user.packageId) || null
  })

  const userTier = computed((): Tier | null => {
    if (!userPackage.value?.tier) return null
    const tier = userPackage.value.tier as string
    if (tier === 'scout' || tier === 'investor' || tier === 'syndicate') {
      return tier
    }
    return null
  })

  const featureLimits = computed((): FeatureLimits | null => {
    if (!userTier.value) return null
    return TIER_FEATURES[userTier.value]
  })

  const hasFeature = (feature: keyof FeatureLimits): boolean => {
    if (!featureLimits.value) return false
    const value = featureLimits.value[feature]
    return typeof value === 'boolean' ? value : value !== null
  }

  const canAccessFeature = (feature: keyof FeatureLimits): boolean => {
    const authStore = useAuthStore()
    if (authStore.getIsAdmin) return true
    return hasFeature(feature)
  }

  const getMaxProperties = (): number | null => {
    const authStore = useAuthStore()
    if (authStore.getIsAdmin) return null
    return featureLimits.value?.maxProperties ?? null
  }

  const canAddProperty = async (currentCount: number): Promise<boolean> => {
    const authStore = useAuthStore()
    if (authStore.getIsAdmin) return true

    const maxProps = getMaxProperties()
    if (maxProps === null) return true

    return currentCount < maxProps
  }

  const getTierFeatures = (tier: Tier): FeatureLimits => {
    return TIER_FEATURES[tier]
  }

  const compareTiers = (tier1: Tier, tier2: Tier): number => {
    const tierOrder: Record<Tier, number> = {
      scout: 1,
      investor: 2,
      syndicate: 3,
    }
    return tierOrder[tier1] - tierOrder[tier2]
  }

  return {
    userPackage,
    userTier,
    featureLimits,
    hasFeature,
    canAccessFeature,
    getMaxProperties,
    canAddProperty,
    getTierFeatures,
    compareTiers,
  }
}
